import React, { Fragment, useEffect, useRef, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import editProfileUser from "assets/images/others/host-side-images/editProfileUSer.png";
import { ReactComponent as EditProfilePersonal } from "assets/images/svgs/edit-profiles.svg";
import { ReactComponent as Addircle } from "assets/images/svgs/attendee/add-circle.svg";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import { siteRoutes } from "utils/helpers/enums/routes";
import { useNavigate } from "react-router-dom";
import { ReactComponent as RemoveIcon } from "assets/images/svgs/removeIconSvg.svg";
import { CompleteAttendeeProfileDTO } from "utils/helpers/models/attendee/complete-profile.dto";
import FormErrorMessage from "components/forms/form-error-message";
import useAuth from "containers/auth/useHooks";
import useStore from "hooks/useStore";
import useAttendee from "../useHooks";
import { ATTENDEE_APIS } from "libs/apis/attendee.api";
import {
  GENDERS,
  LANGUAGES,
  INDUSTRY_TYPES,
  SKILL_LEVELS,
} from "utils/helpers/enums/shared.enum";
import { MinorDTO } from "utils/helpers/models/attendee/minor.dto";
import {
  warningToaster,
  successToaster,
} from "utils/helpers/common/alert-service";
import useUtils from "hooks/useUtils";
import { FaCheck } from "react-icons/fa";
import AttendeeBreadcrumb from "components/breadcrumbs/AttendeeBreadcrumb";

const ParentGuardianDetails: React.FC = () => {
  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.attendeeHome },
    {
      title: "Member Details",
      path: siteRoutes.parentGuardianDetails,
    },
  ];
  const [formData, setFormData] = useState<CompleteAttendeeProfileDTO>(
    new CompleteAttendeeProfileDTO()
  );
  const navigate = useNavigate();
  const [minors, setMinors] = useState<MinorDTO[]>([]);
  const { setUserCompletionData } = useStore();
  const { uploadFile, getStates, getUserProfile } = useAuth();
  const { updateProfile, getAttendeeMinors } = useAttendee();
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const { getAge } = useUtils();

  // Family member form states
  const [showFamilyForm, setShowFamilyForm] = useState<boolean>(false);
  const [familyFormData, setFamilyFormData] = useState<MinorDTO>(
    new MinorDTO()
  );
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showRetypePassword, setShowRetypePassword] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setValue,
    trigger,
    setError,
  } = useForm<CompleteAttendeeProfileDTO>({
    defaultValues: new CompleteAttendeeProfileDTO(),
  });

  // Family member form
  const {
    register: registerFamily,
    handleSubmit: handleSubmitFamily,
    formState: { errors: familyErrors },
    setValue: setFamilyValue,
    getValues: getFamilyValues,
    watch: watchFamily,
    clearErrors: clearFamilyErrors,
  } = useForm<MinorDTO>();

  const onSubmit: SubmitHandler<CompleteAttendeeProfileDTO> = async (data) => {
    // Validate profile picture before submission
    if (
      !selectedFile &&
      (!formData.profilePicId || formData.profilePicId === 0)
    ) {
      setError("profilePicUrl", {
        type: "required",
        message: "Profile picture is required",
      });
      warningToaster("Please upload a profile picture");
      return;
    }

    data.profileCompleted = true;
    try {
      if (selectedFile instanceof File) {
        const file = await uploadFile(selectedFile);
        if (file && file.id) {
          data.profilePicId = file.id;
          updateProfile(data);
        } else {
          warningToaster("Failed to upload profile picture. Please try again.");
          return;
        }
      } else {
        // Ensure profilePicId is set if it already exists
        if (formData.profilePicId && formData.profilePicId > 0) {
          data.profilePicId = formData.profilePicId;
        }
        updateProfile(data);
      }
    } catch (error) {
      warningToaster("Failed to upload profile picture. Please try again.");
    }
  };

  // Family member form submission
  const onSubmitFamily: SubmitHandler<MinorDTO> = async (data) => {
    if (familyFormData.password === familyFormData.retypePassword) {
      // Prepare clean data for API - remove File objects and client-only fields
      const dataToSend = { ...data };
      delete dataToSend.retypePassword;

      try {
        if (data.profilePicUrl instanceof File) {
          const file = await uploadFile(data.profilePicUrl);
          const { id = null } = file || {};
          if (id) {
            dataToSend.profilePicId = id;
            // Remove the File object from profilePicUrl before sending
            delete dataToSend.profilePicUrl;
          } else {
            warningToaster(
              "Failed to upload profile picture. Please try again."
            );
            return;
          }
        } else {
          // Remove profilePicUrl if it exists (we only send profilePicId)
          delete dataToSend.profilePicUrl;
        }

        // Call API directly to avoid navigation
        const response = await ATTENDEE_APIS.addMinorToProfile([dataToSend]);
        const { status = false, message = "" } = response || {};

        if (status) {
          getUserProfile();
          successToaster(message);

          // Reset family form and return to main section
          setFamilyFormData(new MinorDTO());
          Object.keys(new MinorDTO()).forEach((key) => {
            setFamilyValue(key as keyof MinorDTO, "");
          });
          clearFamilyErrors();
          setShowFamilyForm(false);

          // Refresh the minors list to show the newly added member
          getAttendeeMinors(setMinors);
        } else {
          warningToaster(
            message || "Failed to add family member. Please try again."
          );
        }
      } catch (error) {
        warningToaster("Failed to add family member. Please try again.");
      }
    } else {
      warningToaster("Password must match with confirm password");
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files[0];
    if (file) {
      console.log("Setting file in form data and form values");
      // Update state first
      setSelectedFile(file);
      setFormData({ ...formData, profilePicUrl: URL.createObjectURL(file) });
      // Clear errors and set value - validation will use the file value directly
      clearErrors("profilePicUrl");
      setValue("profilePicUrl", file, { shouldValidate: true });
    }
  };

  const handleChange = (event: any) => {
    const { value, name } = event.target;
    formData[name] = value;
    setValue(name, formData[name], { shouldValidate: true });
    if (name === "state") {
      formData.city = "";
    }
    setFormData({ ...formData });
  };

  // Family member form handlers
  const handleFamilyImageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files[0];
    if (file) {
      // Update state first
      setFamilyFormData({
        ...familyFormData,
        profilePicUrl: URL.createObjectURL(file),
      });
      // Clear errors and set value - validation will use the file value directly
      clearFamilyErrors("profilePicUrl");
      setFamilyValue("profilePicUrl", file, { shouldValidate: true });
    }
  };

  const handleFamilyChange = (event: any) => {
    const { value, name } = event.target;
    // Capitalize first letter of each word for firstName and lastName
    let processedValue = value;
    if (name === "firstName" || name === "lastName") {
      processedValue = value
        .split(" ")
        .map((word: string) => 
          word.length > 0 
            ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() 
            : word
        )
        .join(" ");
    }
    setFamilyValue(name, processedValue, { shouldValidate: true });
    setFamilyFormData({ ...familyFormData, [name]: processedValue });
  };

  const selectedInterests = watchFamily("interest") || [];

  const toggleInterest = (value) => {
    const updated = selectedInterests.includes(value)
      ? selectedInterests.filter((item) => item !== value)
      : [...selectedInterests, value];

    setFamilyValue("interest", updated, { shouldValidate: true });
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  };

  const validateMainForm = async () => {
    const isValid = await trigger();

    if (!isValid) {
      return false;
    }

    // Check for profile picture
    if (
      !selectedFile &&
      (!formData.profilePicId || formData.profilePicId === 0)
    ) {
      setError("profilePicUrl", {
        type: "required",
        message: "Profile picture is required",
      });
      return false;
    }

    const requiredFields = [
      "firstName",
      "lastName",
      "dob",
      "gender",
      "language",
      "address",
      "state",
      "city",
      "zipCode",
    ];
    const missingFields = requiredFields.filter((field) => !formData[field]);

    if (missingFields.length > 0) {
      missingFields.forEach((field) => {
        setError(field as any, {
          type: "required",
          message: `${
            field.charAt(0).toUpperCase() + field.slice(1)
          } is required`,
        });
      });
      return false;
    }

    return true;
  };

  const handleAddFamilyMember = async () => {
    const isValid = await validateMainForm();
    if (isValid) {
      setShowFamilyForm(true);
    }
  };

  useEffect(() => {
    getStates(setStates);
    setUserCompletionData(formData, setFormData, setValue);
    getAttendeeMinors(setMinors);
  }, []);

  useEffect(() => {
    if (!showFamilyForm) {
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined && formData[key] !== null) {
          setValue(key as keyof CompleteAttendeeProfileDTO, formData[key]);
        }
      });
    }
  }, [showFamilyForm, formData, setValue]);

  useEffect(() => {
    if (formData.state) {
      const state = states.find((s) => s.name === formData.state);
      if (state) {
        setCities([...state.cities]);
      }
    }
  }, [states, formData.state]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="container min-h-screen sm:mb-16">
      <AttendeeBreadcrumb links={breadcrumbLinks} />
      <div className="flex flex-col gap-2 mb-8">
        <h1 className="text-3xl font-medium text-blackie">
          {showFamilyForm ? "Add Family Member" : "Parent/Guardian Details"}
        </h1>
        <p className="text-lg font-semibold text-label">
          {showFamilyForm
            ? "Please enter the family member's information"
            : "Please enter your family details including Family Member information"}
        </p>
      </div>

      {!showFamilyForm ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Fragment>
            <div className="flex flex-col items-start w-full pb-5">
              <div className="relative">
                <img
                  src={formData.profilePicUrl || editProfileUser}
                  alt="Profile"
                  className="w-36 h-36 rounded-full object-cover"
                />
                <label
                  htmlFor="profileImage"
                  className="absolute bottom-2 right-0 bg-white shadow p-2 rounded-full cursor-pointer"
                >
                  <EditProfilePersonal />
                </label>
                <input
                  id="profileImage"
                  name="profilePicUrl"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  {...register("profilePicUrl", {
                    required: false,
                    validate: (value) => {
                      // Priority: check the value parameter first (most reliable)
                      // Then check selectedFile state, then existing profilePicId
                      const hasFile =
                        value instanceof File || selectedFile instanceof File;
                      const hasExistingPic =
                        formData.profilePicId && formData.profilePicId > 0;
                      if (!hasFile && !hasExistingPic) {
                        return "Profile picture is required";
                      }
                      return true;
                    },
                  })}
                  onChange={handleImageChange}
                />
              </div>
              <div className="flex flex-col gap-2 items-center">
                <p className="text-base text-label  font-semibold mt-1">
                  Upload Profile Picture
                </p>
              </div>
              {errors.profilePicUrl && (
                <p className="text-red-500 text-sm">
                  {errors.profilePicUrl.message as string}
                </p>
              )}
              {/* <FormErrorMessage error={errors.profilePicUrl} /> */}
            </div>
            <div className="w-full my-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  First Name
                </label>
                <input
                  type="text"
                  className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  {...register("firstName", {
                    required: "First name is required",
                  })}
                  value={formData.firstName}
                  onChange={handleChange}
                />
                {errors.firstName && (
                  <p className="text-red-500 text-sm">
                    {errors.firstName.message}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  Last Name
                </label>
                <input
                  type="text"
                  className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  {...register("lastName", {
                    required: "Last name is required",
                  })}
                  value={formData.lastName}
                  onChange={handleChange}
                />
                {errors.lastName && (
                  <p className="text-red-500 text-sm">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: "Enter a valid email",
                    },
                  })}
                  readOnly
                />
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  Date of Birth
                </label>
                <input
                  type="date"
                  placeholder="Select Date"
                  className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  {...register("dob", {
                    required: "Date of Birth is required",
                    validate: (value) => {
                      if (!value) return "Date of Birth is required";
                      const selectedDate = new Date(value);
                      const currentDate = new Date();
                      const age =
                        currentDate.getFullYear() - selectedDate.getFullYear();
                      const monthDiff =
                        currentDate.getMonth() - selectedDate.getMonth();

                      // Calculate exact age considering month and day
                      const exactAge =
                        monthDiff < 0 ||
                        (monthDiff === 0 &&
                          currentDate.getDate() < selectedDate.getDate())
                          ? age - 1
                          : age;

                      if (exactAge < 13) {
                        return "Minimum age required is 13 years";
                      }
                      return true;
                    },
                  })}
                  max={(() => {
                    const currentDate = new Date();
                    const maxDate = new Date(
                      currentDate.getFullYear() - 13,
                      currentDate.getMonth(),
                      currentDate.getDate()
                    );
                    return maxDate.toISOString().split("T")[0];
                  })()}
                  value={formData.dob}
                  onChange={handleChange}
                />
                {errors.dob && (
                  <p className="text-red-500 text-sm">{errors.dob.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  Gender
                </label>
                <select
                  className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  {...register("gender", { required: "Gender is required" })}
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <option value="">Select Gender</option>
                  {Object.values(GENDERS).map((gender, index) => {
                    return (
                      <option className="capitalize" value={gender} key={index}>
                        {gender}
                      </option>
                    );
                  })}
                </select>
                {errors.gender && (
                  <p className="text-red-500 text-sm">
                    {errors.gender.message}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  Language
                </label>
                <select
                  className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  {...register("language", {
                    required: "Language is required",
                  })}
                  value={formData.language}
                  onChange={handleChange}
                >
                  <option value="">Select Language</option>
                  {Object.values(LANGUAGES).map((lang, index) => {
                    return <option key={index}>{lang}</option>;
                  })}
                </select>
                {errors.language && (
                  <p className="text-red-500 text-sm">
                    {errors.language.message}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  Address
                </label>
                <input
                  type="text"
                  className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor  outline-none"
                  {...register("address", {
                    required: "Address is required",
                  })}
                  value={formData.address}
                  onChange={handleChange}
                />
                {errors.address && (
                  <p className="text-red-500 text-sm">
                    {errors.address.message}
                  </p>
                )}
              </div>

              {/* State Dropdown */}
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  State
                </label>
                <select
                  className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  {...register("state", {
                    required: "State is required",
                  })}
                  value={formData.state}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {states.map((state, index) => (
                    <option key={index} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="text-red-500 text-sm">{errors.state.message}</p>
                )}
              </div>

              {/* City Dropdown */}
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  City
                </label>
                <select
                  className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  {...register("city", {
                    required: "City is required",
                  })}
                  value={formData.city}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {cities.map((city, index) => (
                    <option key={index} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {errors.city && (
                  <p className="text-red-500 text-sm">{errors.city.message}</p>
                )}
              </div>

              {/* Zip Code Input */}
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  Zip Code
                </label>
                <input
                  type="text"
                  className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  {...register("zipCode", {
                    required: "Zip code is required",
                    pattern: {
                      value: /^[0-9]{5}(?:-[0-9]{4})?$/,
                      message: "Please enter a valid ZIP code",
                    },
                  })}
                  value={formData.zipCode}
                  onChange={handleChange}
                />
                {errors.zipCode && (
                  <p className="text-red-500 text-sm">
                    {errors.zipCode.message}
                  </p>
                )}
              </div>
            </div>
            <div>
              <button
                onClick={handleAddFamilyMember}
                type="button"
                className="text-primaryBlueText flex items-center gap-2 bg-inputsBorderColor text-xl px-6 py-4 rounded-full"
              >
                <Addircle /> Add Family Member
              </button>
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-4 pt-5">
                {minors.map((minor, index) => {
                  return (
                    <div
                      className="flex flex-col items-center justify-center p-3 rounded-lg min-w-[115px] h-[115px]  border borer-borderColor relative"
                      key={index}
                    >
                      <div className="hover:scale-110 transition-transform duration-300">
                        <img
                          className="w-[44px] h-[44px] rounded-full"
                          src={minor.profilePicUrl}
                          alt="image"
                        />
                      </div>
                      <h1 className="text-sm font-medium my-1 text-blackie whitespace-nowrap capitalize">
                        {`${minor.firstName} ${minor.lastName}`}
                      </h1>
                      <span className="text-xs  text-labelTextColor">
                        {getAge(minor?.dob)} years old
                      </span>
                      {/* <div className="absolute -top-2.5 -right-3 cursor-pointer">
                    <RemoveIcon />
                  </div> */}
                    </div>
                  );
                })}
              </div>
            </div>
          </Fragment>

          <button
            type="submit"
            className="px-4 py-3 bg-primaryBlueBg text-white rounded-full mt-4 md:mt-8"
          >
            Save & Continue
          </button>
        </form>
      ) : (
        <form onSubmit={handleSubmitFamily(onSubmitFamily)}>
          <div>
            <div className="flex flex-col items-start w-full pb-5">
              <div className="relative">
                <img
                  src={familyFormData.profilePicUrl || editProfileUser}
                  alt="Profile"
                  className="w-36 h-36 rounded-full object-cover"
                />
                <label
                  htmlFor="familyProfilePicUrl"
                  className="absolute bottom-2 right-0 bg-white shadow p-2 rounded-full cursor-pointer"
                >
                  <EditProfilePersonal />
                </label>
                <input
                  id="familyProfilePicUrl"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  {...registerFamily("profilePicUrl", {
                    required: false,
                    validate: (value) => {
                      // Check if a file is selected (value is File) or if there's an existing profilePicUrl
                      const hasFile = value instanceof File;
                      // Check if profilePicUrl exists in formData (could be blob URL or existing URL)
                      const hasExistingPic =
                        familyFormData.profilePicUrl &&
                        (typeof familyFormData.profilePicUrl === "string" ||
                          familyFormData.profilePicUrl instanceof File);
                      if (!hasFile && !hasExistingPic) {
                        return "Profile picture is required";
                      }
                      return true;
                    },
                  })}
                  onChange={handleFamilyImageChange}
                />
              </div>
              <div className="flex flex-col gap-2 items-center">
                <p className="text-base text-label  font-semibold mt-1">
                  Upload Minor Profile Picture
                </p>
              </div>
              {familyErrors.firstName && (
                <p className="text-red-500 text-sm">
                  {familyErrors.profilePicUrl?.message as string}
                </p>
              )}
            </div>
            <div>
              <div className="w-full my-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    First Name
                  </label>
                  <input
                    type="text"
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...registerFamily("firstName", {
                      required: "First name is required",
                    })}
                    value={familyFormData.firstName}
                    onChange={handleFamilyChange}
                  />
                  {familyErrors.firstName && (
                    <p className="text-red-500 text-sm">
                      {familyErrors.firstName.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Last Name
                  </label>
                  <input
                    type="text"
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...registerFamily("lastName", {
                      required: "Last name is required",
                    })}
                    value={familyFormData.lastName}
                    onChange={handleFamilyChange}
                  />
                  {familyErrors.lastName && (
                    <p className="text-red-500 text-sm">
                      {familyErrors.lastName.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    placeholder="Select Date"
                    min={
                      new Date(
                        new Date().setFullYear(new Date().getFullYear() - 18)
                      )
                        .toISOString()
                        .split("T")[0]
                    }
                    max={
                      new Date(
                        new Date().setFullYear(new Date().getFullYear() - 4)
                      )
                        .toISOString()
                        .split("T")[0]
                    }
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...registerFamily("dob", {
                      required: "Date of Birth is required",
                      validate: (value) => {
                        if (!value) return true;
                        const selectedDate = new Date(value);
                        const currentDate = new Date();
                        const age =
                          currentDate.getFullYear() -
                          selectedDate.getFullYear();
                        const monthDiff =
                          currentDate.getMonth() - selectedDate.getMonth();
                        const actualAge = monthDiff < 0 ? age - 1 : age;

                        if (actualAge < 4) {
                          return "Age must be at least 4 years old";
                        }
                        if (actualAge >= 18) {
                          return "Age must be less than 18 years old";
                        }
                        return true;
                      },
                    })}
                    value={familyFormData.dob}
                    onChange={handleFamilyChange}
                  />
                  {familyErrors.dob && (
                    <p className="text-red-500 text-sm">
                      {familyErrors.dob.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Gender
                  </label>
                  <select
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...registerFamily("gender", {
                      required: "Gender is required",
                    })}
                    value={familyFormData.gender}
                    onChange={handleFamilyChange}
                  >
                    <option value="">Select Gender</option>
                    {Object.values(GENDERS).map((gender, index) => {
                      return (
                        <option
                          className="capitalize"
                          value={gender}
                          key={index}
                        >
                          {gender}
                        </option>
                      );
                    })}
                  </select>
                  {familyErrors.gender && (
                    <p className="text-red-500 text-sm">
                      {familyErrors.gender.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Language
                  </label>
                  <select
                    {...registerFamily("language", {
                      required: "Language is required",
                    })}
                    value={familyFormData.language}
                    onChange={handleFamilyChange}
                    className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
                  >
                    <option value="" disabled>
                      Select Language
                    </option>
                    {Object.values(LANGUAGES).map((lang, index) => {
                      return (
                        <option value={lang} key={index}>
                          {lang}
                        </option>
                      );
                    })}
                  </select>
                  {familyErrors.language && (
                    <p className="text-red-500 text-sm">
                      {familyErrors.language.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Skill Level
                  </label>
                  <select
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...registerFamily("skillLevel", {
                      required: "Skill Level is required",
                    })}
                    value={familyFormData.skillLevel}
                    onChange={handleFamilyChange}
                  >
                    <option value="">Select Skill Level</option>
                    {Object.values(SKILL_LEVELS).map((skill, index) => {
                      return (
                        <option
                          className="capitalize"
                          value={skill}
                          key={index}
                        >
                          {skill}
                        </option>
                      );
                    })}
                  </select>
                  {familyErrors.skillLevel && (
                    <p className="text-red-500 text-sm">
                      {familyErrors.skillLevel.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1 relative" ref={dropdownRef}>
                  <label className="text-label text-base font-semibold">
                    Interest
                  </label>
                  <div
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full flex items-center px-3 py-3 rounded-lg border border-gray-300 outline-none"
                  >
                    {selectedInterests.length > 0
                      ? selectedInterests.length >= 3
                        ? `${selectedInterests.length} industries selected`
                        : selectedInterests
                            .map((interest) => interest.replace(/_/g, " "))
                            .join(", ")
                      : "Select Industry"}
                  </div>
                  {isDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {Object.values(INDUSTRY_TYPES).map((interest, index) => (
                        <div
                          key={index}
                          onClick={() => toggleInterest(interest)}
                          className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          <span>{interest.replace(/_/g, " ")}</span>
                          {selectedInterests.includes(interest) && (
                            <FaCheck className="text-green-500 text-sm" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <input
                    type="hidden"
                    {...registerFamily("interest", {
                      required: "Select at least one interest",
                      validate: (value) =>
                        Array.isArray(value) && value.length > 0
                          ? true
                          : "Select at least one interest",
                    })}
                  />

                  {familyErrors.interest && (
                    <p className="text-sm text-red-500 mt-1">
                      {familyErrors.interest.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div>
            <span className="text-blackie text-xl font-medium mt-5">
              Minor Credential Information
            </span>
            <div className="w-full my-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex flex-col gap-1">
                <label className="text-label text-base font-semibold">
                  Username
                </label>
                <input
                  type="text"
                  className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  {...registerFamily("username", {
                    required: "Username is required",
                    minLength: {
                      value: 3,
                      message: "Username must be at least 3 characters",
                    },
                    maxLength: {
                      value: 20,
                      message: "Username must not exceed 20 characters",
                    },
                    validate: (value) => {
                      if (!value) return true;
                      // Allow only alphanumeric characters, '-', '_', and '.'
                      // No spaces or other symbols
                      const usernamePattern = /^[a-zA-Z0-9._-]+$/;
                      if (!usernamePattern.test(value)) {
                        return "Username can only contain letters, numbers, '-', '_'";
                      }
                      // Check for spaces
                      if (/\s/.test(value)) {
                        return "Username cannot contain spaces";
                      }
                      return true;
                    },
                  })}
                  value={familyFormData.username}
                  onChange={handleFamilyChange}
                />
                {familyErrors.username && (
                  <p className="text-red-500 text-sm">
                    {familyErrors.username.message}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <label className="text-label text-base font-semibold">
                    Set password
                  </label>
                  <div
                    className="cursor-pointer flex items-center text-label"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <>
                        <AiFillEyeInvisible size={18} className="mr-1" />
                        <span>Hide</span>
                      </>
                    ) : (
                      <>
                        <AiFillEye size={18} className="mr-1" />
                        <span>Show</span>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    {...registerFamily("password", {
                      required: "Password is required",
                      minLength: {
                        value: 8,
                        message: "Password must be at least 8 characters long",
                      },
                      validate: (value) => {
                        if (/\s/.test(value)) {
                          return "Password cannot contain spaces";
                        }
                        if (!/(?=.*[a-z])/.test(value)) {
                          return "Password must include at least one lowercase letter";
                        }
                        if (!/(?=.*[A-Z])/.test(value)) {
                          return "Password must include at least one capital letter";
                        }
                        if (!/(?=.*\d)/.test(value)) {
                          return "Password must include at least one number";
                        }
                        if (!/(?=.*[^\w\s])/.test(value)) {
                          return "Password must include at least one special character";
                        }
                        return true;
                      },
                    })}
                    value={familyFormData.password}
                    onChange={handleFamilyChange}
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  />
                </div>
                {familyErrors.password && (
                  <span className="text-red-500 text-sm">
                    {familyErrors.password.message}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <label className="text-label text-base font-semibold">
                    Retype password
                  </label>
                  <div
                    className="cursor-pointer flex items-center text-label"
                    onClick={() => setShowRetypePassword(!showRetypePassword)}
                  >
                    {showRetypePassword ? (
                      <>
                        <AiFillEyeInvisible size={18} className="mr-1" />
                        <span>Hide</span>
                      </>
                    ) : (
                      <>
                        <AiFillEye size={18} className="mr-1" />
                        <span>Show</span>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <input
                    type={showRetypePassword ? "text" : "password"}
                    placeholder="Retype your password"
                    {...registerFamily("retypePassword", {
                      required: "Retyping password is required",
                      validate: (value) => {
                        if (/\s/.test(value)) {
                          return "Password cannot contain spaces";
                        }
                        return true;
                      },
                    })}
                    value={familyFormData.retypePassword}
                    onChange={handleFamilyChange}
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  />
                </div>
                {familyErrors.retypePassword && (
                  <span className="text-red-500 text-sm">
                    {familyErrors.retypePassword.message}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => {
                // Reset family form data when canceling
                setFamilyFormData(new MinorDTO());
                Object.keys(new MinorDTO()).forEach((key) => {
                  setFamilyValue(key as keyof MinorDTO, "");
                });
                setShowFamilyForm(false);
              }}
              className="px-4 py-2 bg-gray-500 text-white rounded-full"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primaryBlueBg text-white rounded-full"
            >
              Add Family Member
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ParentGuardianDetails;
